import { Box, Typography } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AsyncActionStatus } from '../../actions/common.actionTypes';
import { palette, spaces } from '../../assets/styles/themes/tokens';
import { usePinnedContents, usePinnedStatus } from '../../reducers/pinned.selectors';
import type { Collection, ContentItem } from '../../types/AppContent';
import useCollectionActions from '../../utils/useCollectionActions';
import OwnCollectionOverviewWatchlistEmptyState from './OwnCollectionOverviewWatchlistEmptyState';
import OwnCollectionOverviewWatchlistItemsList from './OwnCollectionOverviewWatchlistItemsList';

interface OwnCollectionOverviewWatchlistProps {
	collection: Collection;
}
const OwnCollectionOverviewWatchlist = ({ collection }: OwnCollectionOverviewWatchlistProps) => {
	const { t } = useTranslation();
	const pinnedStatus = usePinnedStatus();
	const pinnedCollections: ContentItem[] = usePinnedContents();
	const { addItem, removeItem } = useCollectionActions(collection.id);

	const [selectedIds, setSelectedIds] = useState<Set<string>>(() => {
		const ids = new Set<string>();
		pinnedCollections.forEach((item) => {
			const isItemInCollection = collection.contents?.some((c) => c.id === item.id);
			if (isItemInCollection) {
				ids.add(item.id);
			}
		});
		return ids;
	});

	const handleItemClick = useCallback(
		(item: ContentItem) => {
			setSelectedIds((prev) => {
				const next = new Set(prev);
				if (next.has(item.id)) {
					next.delete(item.id);
					removeItem(item);
				} else {
					next.add(item.id);
					addItem(item);
				}
				return next;
			});
		},
		[addItem, removeItem]
	);

	if (pinnedStatus === AsyncActionStatus.LOADING) {
		return null;
	}

	return (
		<Box
			sx={{
				height: 'auto',
				padding: `${spaces.lg} ${spaces.md}`,
				backgroundColor: palette.background.paperElevation2,
				border: `1px solid ${palette.elevation.outlined}`,
			}}
		>
			<Box
				sx={{
					paddingTop: spaces.md,
					paddingBottom: spaces.sm,
					marginBottom: spaces.md,
					textAlign: 'center',
				}}
			>
				<Typography
					variant="overline"
					sx={{
						textTransform: 'uppercase',
					}}
				>
					{t('Add to watchlist')}
				</Typography>
			</Box>
			{pinnedCollections?.length === 0 ? (
				<OwnCollectionOverviewWatchlistEmptyState />
			) : (
				<OwnCollectionOverviewWatchlistItemsList
					pinnedCollections={pinnedCollections}
					selectedIds={selectedIds}
					getItemClickHandler={(item) => () => handleItemClick(item)}
				/>
			)}
		</Box>
	);
};

export default OwnCollectionOverviewWatchlist;
